import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { updateDossier } from '../../api/forumAPI';
import {
  Banknote,
  MapPin,
  User,
  Calendar,
  Building,
  Pencil,
  AlertCircle,
  Check,
  X,
  FileText,
  MessageSquare,
  Send,
  Trash2,
  Landmark,
  History,
  Coins,
  Gavel,
  AlignLeft,
  BookOpen
} from 'lucide-react';
import { OPERATION_TYPES_LABELS } from '../../../../constants/operationTypes';
import CustomSelect from '../../../../components/ui/CustomSelect';
import { useNotification } from '../../../../context/NotificationContext';
import Tooltip from '../../../../components/ui/Tooltip';

export default function DossierOverview({ dossier, onRefresh }) {
  const { showToast } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: dossier.title || '',
    operationType: dossier.operationType || '',
    requestedAmount: dossier.requestedAmount || '',
    estimatedAssetValue: dossier.estimatedAssetValue || '',
    location: dossier.location || '',
    prospectName: dossier.prospectName || '',
    receivedAt: dossier.receivedAt ? new Date(dossier.receivedAt).toISOString().split('T')[0] : '',
    priority: dossier.priority || 'MEDIUM',
    currency: dossier.currency || 'EUR',
    description: dossier.description || ''
  });

  const parseNotes = (rawNotes) => {
    if (!rawNotes) return [];
    try {
      const parsed = JSON.parse(rawNotes);
      if (Array.isArray(parsed)) return parsed;
      return [{ id: 'legacy', text: rawNotes, author: 'Système', date: new Date().toISOString() }];
    } catch (e) {
      return [{ id: 'legacy', text: rawNotes, author: 'Système', date: new Date().toISOString() }];
    }
  };

  const [parsedNotes, setParsedNotes] = useState(() => parseNotes(dossier.memberNotes));
  const [newNote, setNewNote] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');

  useEffect(() => {
    setParsedNotes(parseNotes(dossier.memberNotes));
    setFormData({
      title: dossier.title || '',
      operationType: dossier.operationType || '',
      requestedAmount: dossier.requestedAmount || '',
      estimatedAssetValue: dossier.estimatedAssetValue || '',
      location: dossier.location || '',
      prospectName: dossier.prospectName || '',
      receivedAt: dossier.receivedAt ? new Date(dossier.receivedAt).toISOString().split('T')[0] : '',
      priority: dossier.priority || 'MEDIUM',
      currency: dossier.currency || 'EUR',
      description: dossier.description || ''
    });
  }, [dossier]);

  const typeOptions = Object.entries(OPERATION_TYPES_LABELS).map(([key, label]) => ({
    value: key,
    label: label
  }));

  const priorityOptions = [
    { value: 'LOW', label: 'Basse' },
    { value: 'MEDIUM', label: 'Moyenne' },
    { value: 'HIGH', label: 'Haute' },
    { value: 'CRITICAL', label: 'Critique' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await updateDossier(dossier.id, formData);
      showToast("Dossier mis à jour avec succès", "success");
      if (onRefresh) onRefresh();
      setIsEditing(false);
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      showToast("Erreur lors de la sauvegarde du dossier", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setIsSavingNotes(true);
    try {
      const noteToAdd = {
        id: Date.now().toString(),
        text: newNote.trim(),
        author: dossier.assignedUser?.Nom || 'Équipe',
        date: new Date().toISOString(),
      };
      const updatedNotes = [noteToAdd, ...parsedNotes];
      await updateDossier(dossier.id, { memberNotes: JSON.stringify(updatedNotes) });
      setParsedNotes(updatedNotes);
      setNewNote('');
      showToast("Note ajoutée", "success");
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Erreur lors de l'ajout de la note:", error);
      showToast("Erreur lors de l'ajout de la note", "error");
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleDeleteNote = async (id) => {
    setIsSavingNotes(true);
    try {
      const updatedNotes = parsedNotes.filter(n => n.id !== id);
      await updateDossier(dossier.id, { memberNotes: JSON.stringify(updatedNotes) });
      setParsedNotes(updatedNotes);
      showToast("Note supprimée", "success");
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Erreur lors de la suppression de la note:", error);
      showToast("Erreur lors de la suppression", "error");
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleEditNote = async () => {
    if (!editNoteText.trim()) return;
    setIsSavingNotes(true);
    try {
      const updatedNotes = parsedNotes.map(n =>
        n.id === editingNoteId ? { ...n, text: editNoteText.trim() } : n
      );
      await updateDossier(dossier.id, { memberNotes: JSON.stringify(updatedNotes) });
      setParsedNotes(updatedNotes);
      setEditingNoteId(null);
      setEditNoteText('');
      showToast("Note modifiée", "success");
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Erreur lors de la modification de la note:", error);
      showToast("Erreur lors de la modification", "error");
    } finally {
      setIsSavingNotes(false);
    }
  };

  const cancelEdit = () => {
    setFormData({
      title: dossier.title || '',
      operationType: dossier.operationType || '',
      requestedAmount: dossier.requestedAmount || '',
      estimatedAssetValue: dossier.estimatedAssetValue || '',
      location: dossier.location || '',
      prospectName: dossier.prospectName || '',
      receivedAt: dossier.receivedAt ? new Date(dossier.receivedAt).toISOString().split('T')[0] : '',
      priority: dossier.priority || 'MEDIUM',
      currency: dossier.currency || 'EUR',
      description: dossier.description || ''
    });
    setIsEditing(false);
  };



  const displayClient = dossier.famille?.nom
    ? `Famille ${dossier.famille.nom} ${dossier.famille.prenom || ''}`
    : dossier.societe?.denomination
      ? `Société ${dossier.societe.denomination}`
      : dossier.prospectName
        ? `${dossier.prospectName} (Prospect)`
        : 'Aucun client associé';

  const formatCurrency = (amount) => {
    if (!amount) return 'N/C';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: dossier.currency || 'EUR' }).format(amount);
  };



  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

      {/* Colonne Gauche (2/3) */}
      <div className="lg:col-span-2 space-y-6">

        <div className="workspace-card p-6 relative overflow-hidden">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-primary flex items-center">
              <BookOpen className="w-5 h-5 mr-2 text-tertiary" />
              Informations Générales
            </h3>
            {!isEditing && (
              <Tooltip content="Éditer les informations">
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-tertiary hover:text-primary hover:bg-surface rounded-md transition-colors"
                >
                  <Pencil className="w-5 h-5" />
                </button>
              </Tooltip>
            )}
          </div>

          <AnimatePresence mode="wait">
            {!isEditing ? (
              <motion.div
                key="view"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8"
              >
                <div>
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                    <FileText className="w-4 h-4 mr-1.5" /> Titre du dossier
                  </h4>
                  <p className="text-base text-primary font-medium">{dossier.title || 'N/C'}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                    <Building className="w-4 h-4 mr-1.5" /> Type d'opération
                  </h4>
                  <p className="text-base text-primary font-medium">
                    {OPERATION_TYPES_LABELS[dossier.operationType] || dossier.operationType || 'N/C'}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                    <Banknote className="w-4 h-4 mr-1.5" /> Montant recherché
                  </h4>
                  <p className="text-base text-primary font-medium">{formatCurrency(dossier.requestedAmount)}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                    <Landmark className="w-4 h-4 mr-1.5" /> Valeur estimée de l'actif
                  </h4>
                  <p className="text-base text-primary font-medium">{formatCurrency(dossier.estimatedAssetValue)}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                    <MapPin className="w-4 h-4 mr-1.5" /> Localisation
                  </h4>
                  <p className="text-base text-primary font-medium">{dossier.location || 'Non renseignée'}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                    <User className="w-4 h-4 mr-1.5" /> Client associé
                  </h4>
                  <p className="text-base text-primary font-medium">{displayClient}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                    <Calendar className="w-4 h-4 mr-1.5" /> Date de réception
                  </h4>
                  <p className="text-base text-primary font-medium">{dossier.receivedAt ? new Date(dossier.receivedAt).toLocaleDateString('fr-FR') : 'N/C'}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                    <History className="w-4 h-4 mr-1.5" /> Dernière mise à jour
                  </h4>
                  <p className="text-base text-primary font-medium">{dossier.updatedAt ? new Date(dossier.updatedAt).toLocaleDateString('fr-FR') : 'N/C'}</p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                    <AlertCircle className="w-4 h-4 mr-1.5" /> Priorité
                  </h4>
                  <p className="text-base text-primary font-medium">
                    {dossier.priority === 'CRITICAL' ? 'Critique' : dossier.priority === 'HIGH' ? 'Haute' : dossier.priority === 'MEDIUM' ? 'Moyenne' : 'Basse'}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                    <Coins className="w-4 h-4 mr-1.5" /> Devise
                  </h4>
                  <p className="text-base text-primary font-medium">{dossier.currency || 'EUR'}</p>
                </div>



                {dossier.decisionAt && new Date(dossier.decisionAt).getFullYear() > 1970 && (
                  <div>
                    <h4 className="text-sm font-semibold text-tertiary flex items-center mb-1">
                      <Gavel className="w-4 h-4 mr-1.5" /> Date de décision
                    </h4>
                    <p className="text-base text-primary font-medium">{new Date(dossier.decisionAt).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
                
                <div className="md:col-span-2 mt-2 pt-4 border-t border-workspace">
                  <h4 className="text-sm font-semibold text-tertiary flex items-center mb-3">
                    <AlignLeft className="w-4 h-4 mr-1.5 text-primary" /> Description globale de l'opération
                  </h4>
                  {dossier.description ? (
                    <p className="text-base text-secondary whitespace-pre-wrap leading-relaxed bg-surface p-5 rounded-xl border-l-4 border-l-[var(--hp-bronze-500)] shadow-hp-sm">
                      {dossier.description}
                    </p>
                  ) : (
                    <p className="text-sm italic text-tertiary">Aucune description renseignée pour ce dossier.</p>
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="edit"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="space-y-5 bg-surface-muted p-5 rounded-lg border border-workspace"
              >
                <div>
                  <label className="block text-sm font-semibold text-primary mb-1">Titre du dossier</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="workspace-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-1">Type d'opération</label>
                  <CustomSelect
                    value={formData.operationType}
                    onChange={(val) => setFormData(prev => ({ ...prev, operationType: val }))}
                    options={typeOptions}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-1">Montant recherché</label>
                    <input
                      type="number"
                      name="requestedAmount"
                      value={formData.requestedAmount}
                      onChange={handleChange}
                      className="workspace-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-1">Valeur estimée de l'actif</label>
                    <input
                      type="number"
                      name="estimatedAssetValue"
                      value={formData.estimatedAssetValue}
                      onChange={handleChange}
                      className="workspace-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-1">Localisation</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="workspace-input"
                    placeholder="Ex: Paris 8ème"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-1">
                    Prospect / Initiateur externe
                    <span className="text-xs text-tertiary ml-2 font-normal">(Si non rattaché à un client)</span>
                  </label>
                  <input
                    type="text"
                    name="prospectName"
                    value={formData.prospectName}
                    onChange={handleChange}
                    className="workspace-input"
                    placeholder="Ex: Jean Dupont"
                    disabled={!!dossier.familleId || !!dossier.societeId}
                    title={dossier.familleId || dossier.societeId ? "Un client est déjà rattaché à ce dossier." : ""}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-primary mb-1">Date de réception</label>
                  <input
                    type="date"
                    name="receivedAt"
                    value={formData.receivedAt}
                    onChange={handleChange}
                    className="workspace-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-1">Devise</label>
                    <input
                      type="text"
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      className="workspace-input"
                      placeholder="Ex: EUR"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-primary mb-1">Priorité</label>
                    <CustomSelect
                      value={formData.priority}
                      onChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                      options={priorityOptions}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-end mb-1">
                    <label className="block text-sm font-semibold text-primary">Description globale</label>
                    <span className="text-xs text-tertiary">{formData.description.length}/250</span>
                  </div>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    maxLength={250}
                    rows={4}
                    className="workspace-input w-full resize-y"
                    placeholder="Saisissez le but principal du dossier (250 caractères max)..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-workspace mt-4">
                  <button
                    onClick={cancelEdit}
                    className="btn btn-secondary flex items-center"
                    disabled={isSubmitting}
                  >
                    <X className="w-4 h-4 mr-1.5" />
                    Annuler
                  </button>
                  <button
                    onClick={handleSave}
                    className="btn btn-primary flex items-center"
                    disabled={isSubmitting}
                  >
                    <Check className="w-4 h-4 mr-1.5" />
                    {isSubmitting ? 'Sauvegarde...' : 'Enregistrer'}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Colonne Droite (1/3) */}
      <div className="lg:col-span-1 space-y-6">

        {/* Notes d'équipe (Trello-like) */}
        <div className="workspace-card p-6 flex flex-col h-full">
          <h3 className="text-lg font-bold text-primary flex items-center mb-4 shrink-0">
            <MessageSquare className="w-5 h-5 mr-2 text-tertiary" />
            Notes d'équipe
          </h3>

          <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2 custom-scrollbar">
            {parsedNotes.length === 0 ? (
              <p className="text-sm text-tertiary italic text-center py-8">Aucune note pour le moment.</p>
            ) : (
              parsedNotes.map((note) => (
                <motion.div
                  layout
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  key={note.id}
                  className="bg-surface-muted p-3 rounded-lg border border-workspace group"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 rounded-full bg-secondary text-white flex items-center justify-center text-[10px] font-bold">
                        {note.author ? note.author.charAt(0).toUpperCase() : 'E'}
                      </div>
                      <div>
                        <span className="block text-xs font-semibold text-primary">{note.author}</span>
                        <span className="block text-[10px] text-tertiary">
                          {note.date ? new Date(note.date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => {
                          setEditingNoteId(note.id);
                          setEditNoteText(note.text);
                        }}
                        className="p-1 text-tertiary hover:text-primary transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="p-1 text-tertiary hover:text-danger-500 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {editingNoteId === note.id ? (
                    <div className="mt-2">
                      <textarea
                        className="workspace-input w-full resize-none text-sm p-2 mb-2"
                        rows="2"
                        value={editNoteText}
                        onChange={(e) => setEditNoteText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleEditNote();
                          }
                        }}
                      />
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setEditingNoteId(null)}
                          className="text-xs text-tertiary hover:text-primary"
                        >
                          Annuler
                        </button>
                        <button
                          onClick={handleEditNote}
                          disabled={isSavingNotes || !editNoteText.trim()}
                          className="text-xs text-primary font-semibold"
                        >
                          {isSavingNotes ? 'Enregistrement...' : 'Enregistrer'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-secondary whitespace-pre-wrap">{note.text}</p>
                  )}
                </motion.div>
              ))
            )}
          </div>

          <div className="shrink-0 pt-3 border-t border-workspace flex flex-col gap-2">
            <textarea
              className="workspace-input w-full resize-none text-sm"
              rows="2"
              placeholder="Ajouter une nouvelle note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAddNote();
                }
              }}
            />
            <div className="flex justify-end">
              <button
                onClick={handleAddNote}
                disabled={isSavingNotes || !newNote.trim()}
                className="btn btn-primary py-1.5 px-4 text-xs flex items-center"
              >
                <Send className="w-3 h-3 mr-2" />
                {isSavingNotes ? 'Envoi...' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
